import { Box, Button, Flex, FloraCheckbox, Input } from '@grupoboticario/flora-react';
import { TruckIcon } from '@grupoboticario/flora-react-icons';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { ResponsiveFloraTypography } from 'components/ResponsiveFloraTypography';
import { useTracking, useChangeAddressForm } from 'hooks';
import { useRegistrationControllerContext } from 'store';

import Styles from '../../../Drawers.styles';
import { PrivacyPolicy } from '../components';
import { ErrorInputMessage } from '../components/ErrorInputMessage';
import { SuccessInputMessage } from '../components/SucessInputMessage';

import type { ChangeAddress } from 'domains/change-cadastral';

const MAX_LENGTH_DEFAULT = 10;
const MIN_LENGTH_ZIP_CODE = 9;
const MIN_LENGTH_ADDRESS = 5;
const MAX_LENGTH_ADDRESS = 128;

export const AddressForm = () => {
  const { sendDataToGTM } = useTracking();
  const navigate = useNavigate();
  const {
    canSubmit,
    handleTracking,
    handleOnChangeNumber,
    isSuccess: zipCodeSuccess,
    isError: zipCodeError,
    handleSubmit,
    register,
    addressLength,
    complementLength,
    handleHasNoNumber,
    hasNoNumber,
    state,
    city,
    number,
    numbertLength,
    invalidNumber,
    isZipCodeInvalid,
    isAddressInvalid,
    isDisabledAddress,
    isDisabledDistrict,
    formState: { errors },
  } = useChangeAddressForm();

  const { steps, handleForm } = useRegistrationControllerContext();

  const onSubmit = (data: ChangeAddress) => {
    handleForm({
      ...data,
      number,
      hasNoNumber,
      complement: data.complement ?? '',
      state: state ?? '',
      city: city ?? '',
    });
    navigate('/perfil/editar/endereco/modal-confirmacao', {
      replace: true,
    });
  };

  const handleDontKnowMyCEP = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_alterar_endereco',
        cd_interaction_detail: 'click:nao-sei-meu-cep',
      },
    });
  };

  if (!steps.start || !steps.validate || !steps.confirmation) {
    return <Navigate to='/perfil' replace />;
  }

  return (
    <Styles.DrawerSection
      gap='$6'
      direction='column'
      data-autotagging-flora-section='alterar_endereco'
    >
      <ResponsiveFloraTypography>Insira o CEP do seu novo endereço.</ResponsiveFloraTypography>
      <Styles.FullFlex direction='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            id='zipcode'
            data-title='input:cep'
            labelText='Inserir CEP:'
            mask='xxxxx-xxx'
            invalid={zipCodeError || isZipCodeInvalid}
            required
            {...register('zipcode', { minLength: MIN_LENGTH_ZIP_CODE, onBlur: handleTracking })}
          />
          {!zipCodeSuccess &&
            !zipCodeError &&
            (errors.zipcode?.type === 'minLength' || isZipCodeInvalid) && (
              <ErrorInputMessage message='O CEP deve conter 8 dígitos.' />
            )}
          {zipCodeError && (
            <ErrorInputMessage message='CEP não encontrado. Verifique se você digitou corretamente.' />
          )}
          {zipCodeSuccess && (
            <SuccessInputMessage message='CEP encontrado. Preencha os campos obrigatórios.' />
          )}

          <Flex gap='$4' direction='column'>
            <Link
              to='https://buscacepinter.correios.com.br/app/endereco/index.php'
              target='_blank'
              rel='noopener noreferrer'
            >
              <ResponsiveFloraTypography
                onClick={handleDontKnowMyCEP}
                color='$backgroundAltPrimary'
                fontSize='bodySmallShort'
                fontWeight='regular'
                textDecoration='underline'
              >
                Não sei meu CEP
              </ResponsiveFloraTypography>
            </Link>

            <Input
              data-title='input:cidade-estado'
              id='city'
              labelText='Cidade/Estado:'
              disabled
              required
              {...register('city', { onBlur: handleTracking })}
            />
          </Flex>
          <Flex direction='column'>
            <Input
              id='street'
              data-title='input:rua'
              labelText='Endereço: Ex: Rua, Avenida'
              invalid={zipCodeSuccess && isAddressInvalid}
              required
              {...register('address', { onBlur: handleTracking })}
              disabled={isDisabledAddress}
              minLength={MIN_LENGTH_ADDRESS}
              maxLength={MAX_LENGTH_ADDRESS}
            />
            <Flex justify='space-between' css={{ width: '100%' }}>
              {zipCodeSuccess && isAddressInvalid ? (
                <ErrorInputMessage
                  message={`Mínimo de ${MIN_LENGTH_ADDRESS} caracteres não atingido.`}
                />
              ) : (
                <Box />
              )}
              {zipCodeSuccess && addressLength === MAX_LENGTH_ADDRESS ? (
                <SuccessInputMessage
                  message={`Limite máximo de ${MAX_LENGTH_ADDRESS} caracteres atingido.`}
                />
              ) : (
                <Box />
              )}

              <ResponsiveFloraTypography
                fontSize='exceptionsAuxiliar'
                color='$nonInteractivePredominant'
                fontWeight='bold'
                css={{ position: 'relative', bottom: '$3', alignItems: 'right' }}
              >
                {addressLength || '0'}/{MAX_LENGTH_ADDRESS}
              </ResponsiveFloraTypography>
            </Flex>
          </Flex>
          <Input
            id='district'
            data-title='input:bairro'
            labelText='Bairro: Ex: Centro'
            required
            {...register('district', { onBlur: handleTracking })}
            disabled={isDisabledDistrict}
          />
          <Flex align='center' justify='flex-start' gap='$5'>
            <Flex css={{ width: '50% ' }} direction='column'>
              <Input
                id='number'
                data-title='input:numero'
                labelText='Número: Ex: 1000'
                mask={(value) => value.replace(/\D/g, '')}
                value={number}
                required={!hasNoNumber}
                disabled={hasNoNumber || !zipCodeSuccess}
                maxLength={MAX_LENGTH_DEFAULT}
                {...register('number', { onBlur: handleTracking, onChange: handleOnChangeNumber })}
              />
              <Flex justify='space-between' css={{ width: '100%' }}>
                <Box />
                <ResponsiveFloraTypography
                  fontSize='exceptionsAuxiliar'
                  color='$nonInteractivePredominant'
                  fontWeight='bold'
                  css={{ position: 'relative', bottom: '$3', alignItems: 'right' }}
                >
                  {numbertLength || '0'}/{MAX_LENGTH_DEFAULT}
                </ResponsiveFloraTypography>
              </Flex>
            </Flex>

            <Flex gap='$2' css={{ position: 'relative', marginBottom: '$4' }}>
              <FloraCheckbox
                id='hasNoNumber'
                data-title={hasNoNumber ? 'uncheck:sem-numero' : 'check:sem-numero'}
                checked={hasNoNumber}
                onChange={handleHasNoNumber}
                disabled={!zipCodeSuccess}
                label='Sem número'
              />
            </Flex>
          </Flex>
          {!invalidNumber && numbertLength === MAX_LENGTH_DEFAULT && (
            <SuccessInputMessage
              message={`Limite máximo de ${MAX_LENGTH_DEFAULT} caracteres atingido.`}
            />
          )}
          {invalidNumber && (
            <ErrorInputMessage message={`Endereço sem número? Selecione a opção 'Sem número'.`} />
          )}
          <Flex direction='column'>
            <Input
              id='complement'
              data-title='input:complemento'
              labelText='Complemento: (opcional)'
              {...register('complement', { onBlur: handleTracking })}
              maxLength={MAX_LENGTH_DEFAULT}
              disabled={!zipCodeSuccess}
            />
            <Flex justify='space-between' css={{ width: '100%' }}>
              {complementLength === MAX_LENGTH_DEFAULT ? (
                <SuccessInputMessage
                  message={`Limite máximo de ${MAX_LENGTH_DEFAULT} caracteres atingido.`}
                />
              ) : (
                <Box />
              )}

              <ResponsiveFloraTypography
                fontSize='exceptionsAuxiliar'
                color='$nonInteractivePredominant'
                fontWeight='bold'
                css={{ position: 'relative', bottom: '$3', alignItems: 'right' }}
              >
                {complementLength || '0'}/{MAX_LENGTH_DEFAULT}
              </ResponsiveFloraTypography>
            </Flex>
          </Flex>
          <Flex direction='column' gap='$4'>
            <PrivacyPolicy />
            <Button
              autotagging-flora='on'
              data-flora='button'
              data-flora-text='solicitar-alteracao-de-endereco'
              type='submit'
              leftIcon={<TruckIcon size={16} />}
              disabled={!canSubmit}
            >
              Solicitar alteração de endereço
            </Button>
          </Flex>
        </form>
      </Styles.FullFlex>
    </Styles.DrawerSection>
  );
};
